import React, { useRef, useEffect, useState } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { tradingAgentApi } from '../services/api';
import globalWebSocketService from '../services/globalWebSocketService';

const AgentTerminal = ({ agentId, strategyId }) => {
  const terminalRef = useRef(null);
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [externalId, setExternalId] = useState(null);
  
  console.log(`[AgentTerminal] Component rendered with agentId: ${agentId}, strategyId: ${strategyId}`);
  
  useEffect(() => {
    const fetchExternalId = async () => {
      try {
        if (!agentId) {
          setExternalId(null);
          return;
        }
        
        console.log(`[Terminal] Fetching external_id for agent ${agentId}`);
        
        // First try to get from localStorage
        const cachedStatus = localStorage.getItem(`agent_status_${agentId}`);
        if (cachedStatus) {
          try {
            const parsedStatus = JSON.parse(cachedStatus);
            if (parsedStatus.externalId) {
              console.log(`[Terminal] Using cached external_id: ${parsedStatus.externalId} for agent ${agentId}`);
              setExternalId(parsedStatus.externalId);
              return;
            }
          } catch (e) {
            console.error('[Terminal] Error parsing cached agent status:', e);
          }
        }
        
        // If not in localStorage, fetch from API
        const agentDetails = await tradingAgentApi.getAgentById(agentId);
        
        if (agentDetails && agentDetails.external_id) {
          console.log(`[Terminal] Found external_id: ${agentDetails.external_id} for agent ${agentId}`);
          setExternalId(agentDetails.external_id);
          
          // Store it in localStorage for future use
          localStorage.setItem(`agent_status_${agentId}`, JSON.stringify({
            active: agentDetails.active,
            externalId: agentDetails.external_id,
            strategyId: strategyId
          }));
        } else {
          console.error(`[Terminal] No external_id found for agent ${agentId}`);
          setExternalId(null);
        }
      } catch (error) {
        console.error(`[Terminal] Error fetching agent details:`, error);
        setExternalId(null);
      }
    };
    
    // Clear externalId and fetch new one when agentId changes
    setExternalId(null);
    fetchExternalId();
  }, [agentId]); // Only depend on agentId to avoid unnecessary refetches
  
  // Setup WebSocket to receive real-time updates
  const handleUpdate = async (update) => {
    // Add timestamp to all messages
    const timestamp = new Date().toLocaleTimeString();
    
    // Log raw update for debugging with more detail
    console.log(`[Terminal] WebSocket update received at ${timestamp} for agent ${agentId}:`, update);
    console.log(`[Terminal] Update type: ${typeof update}, length: ${update?.length || 'N/A'}`);
    
    // Try to parse the message if it's a JSON string
    let formattedMessage;
    try {
      if (typeof update === 'string' && (update.startsWith('{') || update.startsWith('['))) {
        const parsed = JSON.parse(update);
        console.log('[Terminal] Parsed WebSocket message:', parsed);
        
        // PRIORITY: Show ALL data from external API by default
        // Check for specific message fields first, but if none found, show everything
        if (parsed.message && typeof parsed.message === 'string') {
          formattedMessage = parsed.message;
        } else if (parsed.update && typeof parsed.update === 'string') {
          formattedMessage = parsed.update;
        } else if (parsed.text && typeof parsed.text === 'string') {
          formattedMessage = parsed.text;
        } else if (parsed.content && typeof parsed.content === 'string') {
          formattedMessage = parsed.content;
        } else if (parsed.response && typeof parsed.response === 'string') {
          formattedMessage = parsed.response;
        } else if (parsed.type === 'strategy-update' && parsed.update) {
          formattedMessage = parsed.update;
        } else {
          // Show the ENTIRE JSON object so user sees ALL external API data
          // This ensures nothing is hidden from the user
          formattedMessage = JSON.stringify(parsed, null, 2);
        }
      } else {
        formattedMessage = update;
      }
    } catch (error) {
      console.warn('[Terminal] Error parsing WebSocket message:', error);
      formattedMessage = update; // Use raw message on error to ensure no data loss
    }
    
    // Format the message with timestamp for display
    const formattedLine = `[${timestamp}] ${formattedMessage}`;
    
    // DON'T save to database here - let the global WebSocket service handle it
    // This prevents duplicate saves
    
    // Add to terminal output with better deduplication
    setTerminalOutput(prev => {
      // More robust duplicate check - check if the actual message content exists
      const messageExists = prev.some(line => {
        // Extract message content from formatted line
        const match = line.match(/^\[.*?\] (.*)$/);
        const existingMessage = match ? match[1] : line;
        return existingMessage === formattedMessage;
      });
      
      if (messageExists) {
        console.log('[Terminal] Duplicate message detected, not adding to terminal:', formattedMessage);
        return prev;
      }
      
      // Log that we're adding this message
      console.log(`[Terminal] Adding message to terminal: ${formattedLine}`);
      
      return [...prev, formattedLine];
    });
  };
  
  // Connect to WebSocket for real-time updates using EXTERNAL_ID instead of strategyId
  // Only connect when we have an externalId or strategyId
  const { connected, error } = useWebSocket(externalId || strategyId, handleUpdate);
  
  console.log(`[Terminal Debug] Agent ${agentId}: externalId=${externalId}, connected=${connected}, error=${error}`);
  
  // Start persistent global WebSocket connection for LOGS
  useEffect(() => {
    if (agentId && externalId) {
      console.log(`[Terminal] Starting persistent WebSocket for LOGS - agent ${agentId}, external ID: ${externalId}`);
      // CRITICAL: Use 'logs' connection type for terminal logs, not 'chat'
      globalWebSocketService.startConnection(agentId, externalId, strategyId, 'logs');
      
      // Return cleanup function
      return () => {
        // Don't stop the connection here - let it persist for background message capture
        console.log(`[Terminal] AgentTerminal unmounting, but keeping persistent connection for agent ${agentId}`);
      };
    }
  }, [agentId, externalId, strategyId]);
  
  // Add database polling to catch messages missed while away
  useEffect(() => {
    if (!agentId) return;
    
    let pollingActive = true;
    
    const pollForMissedMessages = setInterval(async () => {
      if (!pollingActive || !agentId) return;
      
      try {
        // Fetch all logs from database
        const allLogs = await tradingAgentApi.getTerminalLogs(agentId);
        
        if (allLogs && allLogs.length > 0) {
          setTerminalOutput(prev => {
            // Create a map of existing messages for fast lookup
            const existingMessages = new Map();
            prev.forEach(line => {
              const match = line.match(/^\[.*?\] (.*)$/);
              if (match) {
                existingMessages.set(match[1], true);
              }
            });
            
            // Find messages from database that we don't have
            const missingMessages = [];
            allLogs.forEach(log => {
              if (!existingMessages.has(log.message)) {
                const timestamp = new Date(log.timestamp).toLocaleTimeString();
                const formattedLine = `[${timestamp}] ${log.message}`;
                missingMessages.push({ 
                  timestamp: new Date(log.timestamp).getTime(), 
                  formattedLine,
                  message: log.message
                });
              }
            });
            
            if (missingMessages.length > 0) {
              console.log(`[Terminal] Found ${missingMessages.length} missing messages from database`);
              
              // Sort by timestamp
              missingMessages.sort((a, b) => a.timestamp - b.timestamp);
              
              // Build new terminal output maintaining chronological order
              const allMessages = [];
              
              // Add existing messages with their timestamps
              prev.forEach(line => {
                const match = line.match(/^\[(\d{1,2}:\d{1,2}:\d{1,2}(?:\s[AP]M)?)\] (.*)$/);
                if (match) {
                  // Parse the time string to get a comparable timestamp
                  const timeStr = match[1];
                  const message = match[2];
                  allMessages.push({ formattedLine: line, message, timeStr });
                }
              });
              
              // Add missing messages
              missingMessages.forEach(msg => {
                allMessages.push(msg);
              });
              
              // Remove duplicates based on message content
              const uniqueMessages = [];
              const seenMessages = new Set();
              
              allMessages.forEach(msg => {
                const messageContent = msg.message || msg.formattedLine;
                if (!seenMessages.has(messageContent)) {
                  seenMessages.add(messageContent);
                  uniqueMessages.push(msg.formattedLine);
                }
              });
              
              return uniqueMessages;
            }
            
            return prev;
          });
        }
      } catch (error) {
        console.error('[Terminal] Error polling for missed messages:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => {
      pollingActive = false;
      clearInterval(pollForMissedMessages);
    };
  }, [agentId]);
  
  // Fetch logs when agent changes OR when externalId becomes available
  useEffect(() => {
    const fetchTerminalLogs = async () => {
      if (agentId) {
        try {
          console.log(`[Terminal] Starting to fetch logs for agent ${agentId}`);
          setTerminalOutput([`[${new Date().toLocaleTimeString()}] Loading terminal history...`]);
          
          // First try to get terminal logs from database
          const terminalLogs = await tradingAgentApi.getTerminalLogs(agentId);
          console.log(`[Terminal] Received ${terminalLogs?.length || 0} terminal logs for agent ${agentId}`);
          
          if (terminalLogs && terminalLogs.length > 0) {
            // Remove duplicates based on message content
            const uniqueLogs = [];
            const seenMessages = new Set();
            
            // Sort by timestamp first (oldest to newest)
            const sortedLogs = [...terminalLogs].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            sortedLogs.forEach(log => {
              if (!seenMessages.has(log.message)) {
                seenMessages.add(log.message);
                uniqueLogs.push(log);
              } else {
                console.log(`[Terminal] Filtering out duplicate log during initial load: ${log.message}`);
              }
            });
            
            // Format and add unique logs to the output
            const formattedLogs = uniqueLogs.map(log => {
              const timestamp = new Date(log.timestamp).toLocaleTimeString();
              return `[${timestamp}] ${log.message}`;
            });
            
            setTerminalOutput(formattedLogs); // Don't reverse - keep chronological order
            console.log(`[Terminal] Loaded ${formattedLogs.length} unique logs from database (filtered ${terminalLogs.length - uniqueLogs.length} duplicates)`);
            return;
          }
          
          // If no terminal logs, fall back to strategy logs
          if (strategyId) {
            const logs = await tradingAgentApi.getStrategyLogs(agentId, strategyId);
            
            if (logs && logs.length > 0) {
              // Format and add logs to the output
              const formattedLogs = logs.map(log => {
                const timestamp = new Date(log.timestamp).toLocaleTimeString();
                return `[${timestamp}] ${log.message}`;
              });
              
              setTerminalOutput(formattedLogs.reverse());
              console.log(`[Terminal] Loaded ${formattedLogs.length} strategy logs`);
              return;
            }
          }
          
          // If neither terminal nor strategy logs are available
          setTerminalOutput([
            `[${new Date().toLocaleTimeString()}] Waiting for strategy to begin...`,
            `[${new Date().toLocaleTimeString()}] Using external ID: ${externalId || 'waiting...'} for WebSocket connection`
          ]);
        } catch (error) {
          console.error('Error fetching logs:', error);
          setTerminalOutput([
            `[${new Date().toLocaleTimeString()}] Error loading terminal history.`,
            `[${new Date().toLocaleTimeString()}] Will attempt to reconnect automatically.`,
            `[${new Date().toLocaleTimeString()}] Using external ID: ${externalId || 'waiting...'} for WebSocket connection`
          ]);
        }
      }
    };
    
    // Only fetch logs when we have an agentId
    if (agentId) {
      console.log(`[Terminal] Fetching logs for agent ${agentId}, externalId: ${externalId}`);
      fetchTerminalLogs();
    }
  }, [agentId, externalId]); // Include externalId so logs refresh when it becomes available
  
  // Force a refresh when externalId becomes available for the first time
  useEffect(() => {
    if (externalId && agentId) {
      console.log(`[Terminal] ExternalId ${externalId} is now available for agent ${agentId}, triggering log refresh`);
      // Small delay to ensure WebSocket has time to connect
      setTimeout(() => {
        tradingAgentApi.getTerminalLogs(agentId).then(logs => {
          if (logs && logs.length > 0) {
            const formattedLogs = logs.map(log => {
              const timestamp = new Date(log.timestamp).toLocaleTimeString();
              return `[${timestamp}] ${log.message}`;
            });
            setTerminalOutput(formattedLogs.reverse());
            console.log(`[Terminal] Force refreshed ${formattedLogs.length} logs after externalId became available`);
          }
        }).catch(err => {
          console.error('[Terminal] Error in force refresh:', err);
        });
      }, 1000);
    }
  }, [externalId]);
  
  useEffect(() => {
    // Auto-scroll to bottom when new content is added
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);
  
  // Clear terminal logs
  const handleClearTerminal = async () => {
    if (!agentId) return;
    
    try {
      await tradingAgentApi.clearTerminalLogs(agentId);
      setTerminalOutput([`[${new Date().toLocaleTimeString()}] Terminal logs cleared`]);
    } catch (error) {
      console.error('Error clearing terminal logs:', error);
      setTerminalOutput(prev => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] Error clearing terminal logs: ${error.message}`
      ]);
    }
  };
  
  // Parse output lines to add syntax highlighting
  const parseOutputLine = (line) => {
    if (line.includes('Error:') || line.includes('error') || line.includes('failed')) {
      return <span className="text-red-400">{line}</span>;
    } else if (line.includes('Starting') || line.includes('Initializing')) {
      return <span className="text-blue-400">{line}</span>;
    } else if (line.includes('Initialized') || line.includes('success')) {
      return <span className="text-green-400">{line}</span>;
    } else if (line.includes('Bought') || line.includes('buying')) {
      return <span className="text-cyan-400">{line}</span>;
    } else if (line.includes('Sold') || line.includes('selling')) {
      return <span className="text-green-400">{line}</span>;
    } else if (line.includes('Placed')) {
      return <span className="text-purple-400">{line}</span>;
    } else if (line.includes('Paused')) {
      return <span className="text-red-400">{line}</span>;
    } else if (line.includes('Resumed')) {
      return <span className="text-green-400">{line}</span>;
    } else if (line.includes('Stopped')) {
      return <span className="text-red-400">{line}</span>;
    } else if (line.includes('Profit:')) {
      const parts = line.split('Profit:');
      return (
        <>
          {parts[0]}
          <span className="text-green-400">Profit:{parts[1]}</span>
        </>
      );
    }
    
    return line;
  };
  
  return (
    <div className="relative h-full flex flex-col">
      {/* WebSocket status indicator - Responsive */}
      <div className="absolute top-1 sm:top-2 right-1 sm:right-2 z-10 flex items-center space-x-1 sm:space-x-2">
        <div className={`flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs ${
          connected ? 'bg-green-800 text-green-200' : 'bg-yellow-800 text-yellow-200'
        }`}>
          <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 mr-1 rounded-full ${
            connected ? 'bg-green-400 animate-pulse' : 'bg-yellow-400 animate-pulse'
          }`}></div>
          <span className="hidden sm:inline">{connected ? 'Live' : 'Polling'}</span>
          <span className="sm:hidden">{connected ? 'L' : 'P'}</span>
        </div>

        {/* Terminal controls - Responsive */}
        <button
          onClick={handleClearTerminal}
          className="bg-gray-700 text-xs text-gray-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full hover:bg-gray-600"
          title="Clear terminal logs"
        >
          <span className="hidden sm:inline">Clear</span>
          <span className="sm:hidden">C</span>
        </button>
        
        {/* Debug info button */}
        {/* <button 
          onClick={() => {
            console.log('[Terminal Debug]', {
              connected,
              error,
              strategyId,
              externalId,
              wsDebug: debug
            });
            alert(`WebSocket debug info logged to console. Using External ID: ${externalId || 'None'}`);
          }}
          className="bg-gray-700 text-xs text-gray-300 px-2 py-1 rounded-full hover:bg-gray-600"
        >
          Debug
        </button> */}
      </div>
      
      <div
        ref={terminalRef}
        className="bg-gray-900 text-green-300 p-2 sm:p-4 font-mono text-xs sm:text-sm overflow-y-auto flex-1 rounded-b-lg"
        style={{ fontSize: 'clamp(10px, 2.5vw, 14px)' }}
      >
        {terminalOutput.length === 0 ? (
          <div className="text-gray-500 italic text-xs sm:text-sm">
            {error ?
              `Unable to connect to trading agent after multiple attempts. Please try again later.` :
              `Waiting for strategy execution... External ID: ${externalId || 'fetching...'}`}
          </div>
        ) : (
          <div className="space-y-0.5 sm:space-y-1">
            {terminalOutput.map((line, index) => (
              <div key={index} className="terminal-line break-words overflow-wrap-anywhere">{parseOutputLine(line)}</div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-2 sm:mt-4 p-2 bg-red-900 text-red-300 rounded text-xs sm:text-sm">
            Connection Error: Unable to connect to trading agent. {error}
            <br />
            External ID: {externalId || 'None'} - Make sure the agent is active
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentTerminal;